import { lazy } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { Layout } from './Layout.jsx';
import { Home } from '../pages/Home.jsx';
import { NotFound } from '../pages/NotFound.jsx';
import { ServerError } from '../pages/ServerError.jsx';

/**
 * The route table.
 *
 * Home is imported eagerly because it is the first paint for most visitors and a
 * lazy chunk there costs a round trip before anything appears. Everything else
 * is split, which is what keeps the entry bundle inside the 200 KB budget.
 *
 * Shop, category, brand listing, and search are the same page with a different
 * fixed constraint — three near-identical listing templates would drift apart
 * within a month.
 */

/**
 * Carries a slug across the journal-to-blogs rename, so an existing link to a
 * post still lands on that post rather than the index.
 */
const RedirectJournal = ({ to }) => {
  const { slug } = useParams();
  return <Navigate to={`${to}/${slug}`} replace />;
};

const page = (loader, name) =>
  lazy(async () => {
    const module = await loader();
    return { default: module[name] ?? module.default };
  });

const Shop = page(() => import('../pages/Shop.jsx'), 'Shop');
const Combos = page(() => import('../pages/Combos.jsx'), 'Combos');
const Product = page(() => import('../pages/Product.jsx'), 'Product');
const Brands = page(() => import('../pages/Brands.jsx'), 'Brands');

const Cart = page(() => import('../pages/Cart.jsx'), 'Cart');
const Checkout = page(() => import('../pages/Checkout.jsx'), 'Checkout');
const OrderConfirmation = page(() => import('../pages/OrderConfirmation.jsx'), 'OrderConfirmation');
const TrackOrder = page(() => import('../pages/TrackOrder.jsx'), 'TrackOrder');
const SignIn = page(() => import('../pages/SignIn.jsx'), 'SignIn');

const Journal = page(() => import('../pages/Journal.jsx'), 'Journal');
const JournalPost = page(() => import('../pages/Journal.jsx'), 'JournalPost');
const About = page(() => import('../pages/About.jsx'), 'About');
const Contact = page(() => import('../pages/Contact.jsx'), 'Contact');
const StoreLocator = page(() => import('../pages/StoreLocator.jsx'), 'StoreLocator');
const CmsPage = page(() => import('../pages/CmsPage.jsx'), 'CmsPage');

const AccountLayout = page(() => import('../pages/account/AccountLayout.jsx'), 'AccountLayout');
const AccountDashboard = page(() => import('../pages/account/Dashboard.jsx'), 'Dashboard');
const AccountOrders = page(() => import('../pages/account/Orders.jsx'), 'Orders');
const AccountOrderDetail = page(() => import('../pages/account/Orders.jsx'), 'OrderDetail');
const AccountAddresses = page(() => import('../pages/account/Addresses.jsx'), 'Addresses');
const AccountWishlist = page(() => import('../pages/account/Wishlist.jsx'), 'Wishlist');
const AccountProfile = page(() => import('../pages/account/Profile.jsx'), 'Profile');

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <ServerError />,
    children: [
      { index: true, element: <Home /> },

      /* catalogue */
      { path: 'shop', element: <Shop mode="all" /> },
      { path: 'category/:slug', element: <Shop mode="category" /> },
      { path: 'brand/:slug', element: <Shop mode="brand" /> },
      { path: 'search', element: <Shop mode="search" /> },
      { path: 'brands', element: <Brands /> },
      { path: 'product/:slug', element: <Product /> },
      { path: 'combos', element: <Combos /> },

      /* buying */
      { path: 'cart', element: <Cart /> },
      { path: 'checkout', element: <Checkout /> },
      { path: 'order/:orderNumber', element: <OrderConfirmation /> },
      { path: 'orders/track', element: <TrackOrder /> },

      /* content */
      { path: 'blogs', element: <Journal /> },
      { path: 'blogs/category/:slug', element: <Journal /> },
      { path: 'blogs/:slug', element: <JournalPost /> },

      // The section was called the journal until it was renamed. Redirecting
      // rather than deleting keeps every link already shared or indexed working.
      { path: 'journal', element: <Navigate to="/blogs" replace /> },
      { path: 'journal/category/:slug', element: <RedirectJournal to="/blogs/category" /> },
      { path: 'journal/:slug', element: <RedirectJournal to="/blogs" /> },
      { path: 'contact', element: <Contact /> },
      { path: 'store-locator', element: <StoreLocator /> },

      /* CMS-backed pages. Each route names the page key it renders, so the
         storefront never has to guess and an operator cannot break a footer
         link by renaming a title. */
      { path: 'about', element: <About /> },
      { path: 'faq', element: <CmsPage pageKey="faq" /> },
      { path: 'terms-and-conditions', element: <CmsPage pageKey="terms" /> },
      { path: 'privacy-policy', element: <CmsPage pageKey="privacy-policy" /> },
      { path: 'refund-and-cancellation-policy', element: <CmsPage pageKey="refund-policy" /> },
      { path: 'shipping-and-delivery-policy', element: <CmsPage pageKey="shipping-policy" /> },
      { path: 'disclaimer', element: <CmsPage pageKey="disclaimer" /> },

      /* account */
      { path: 'sign-in', element: <SignIn /> },
      {
        path: 'account',
        element: <AccountLayout />,
        children: [
          { index: true, element: <AccountDashboard /> },
          { path: 'orders', element: <AccountOrders /> },
          { path: 'orders/:id', element: <AccountOrderDetail /> },
          { path: 'addresses', element: <AccountAddresses /> },
          { path: 'wishlist', element: <AccountWishlist /> },
          { path: 'profile', element: <AccountProfile /> },
        ],
      },

      { path: '*', element: <NotFound /> },
    ],
  },
]);

export default router;
